import { ApiError } from './permitService';

const API_BASE_URL = '/api/v1';

export interface IsolationPoint {
  id: number;
  energy_source: {
    id: number;
    source_name: string;
    source_type: string;
    risk_level: string;
  };
  isolation_equipment: {
    id: number;
    equipment_number: string;
    kks_code: string;
    name: string;
  };
  isolation_type: string;
  isolation_capability: string;
  sequence_number: number;
  is_primary: boolean;
  is_backup: boolean;
  is_emergency: boolean;
  requires_key: boolean;
  key_location: string;
  requires_permit: boolean;
  local_operation: boolean;
  remote_operation: boolean;
  ppe_required: string;
  competency_required: string;
  two_person_operation: boolean;
  operation_time_minutes: number | null;
  verification_method: string;
  restoration_notes: string;
}

export interface IsolationMethod {
  id: number;
  equipment: {
    id: number;
    equipment_number: string;
    kks_code: string;
    name: string;
  };
  method_name: string;
  sequence_number: number | null;
  description: string;
  isolation_type: string;
  requires_verification: boolean;
  verification_method: string;
  tools_required: string;
  ppe_required: string;
  competency_required: string;
  estimated_time_minutes: number | null;
}

export interface IsolationAnalysisResult {
  equipment_analyzed: number;
  energy_sources_count: number;
  isolation_methods_count: number;
  equipment_details: Array<{
    id: number;
    equipment_number: string;
    kks_code: string;
    name: string;
    requires_isolation: boolean;
  }>;
  energy_sources: Array<{
    id: number;
    source_name: string;
    source_type: string;
    risk_level: string;
    isolation_required: boolean;
  }>;
  isolation_analysis: Array<{
    energy_source_id: number;
    energy_source_name: string;
    isolation_point_id: number;
    isolation_equipment_kks: string;
    isolation_equipment_name: string;
    isolation_method: string;
    isolation_type: string;
    sequence_number: number;
    verification_method: string;
    estimated_time_minutes: number;
    ppe_required: string;
    competency_required: string;
    is_primary: boolean;
    requires_key: boolean;
    key_location: string;
  }>;
  suggested_safety_measures: Array<{
    id: number;
    measure_type: string;
    sequence_number: number;
    description: string;
    verification_required: boolean;
    verification_method: string;
  }>;
  analysis_summary: {
    total_isolation_time: number;
    isolation_complexity: string;
    critical_isolations: number;
    requires_two_person: boolean;
    key_controlled_isolations: number;
  };
}

class IsolationService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Analyze isolation requirements for given equipment
   */
  async analyzeEquipmentIsolations(equipmentIds: number[]): Promise<IsolationAnalysisResult> {
    const response = await fetch(`${API_BASE_URL}/sites/site-management/analyze_isolations/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({
        equipment_ids: equipmentIds
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.json();
  }

  /**
   * Get isolation points for specific equipment
   */
  async getEquipmentIsolationPoints(equipmentId: number): Promise<IsolationPoint[]> {
    const response = await fetch(`${API_BASE_URL}/sites/equipment/${equipmentId}/isolation-points/`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.json();
  }

  /**
   * Get isolation methods for specific equipment
   */
  async getEquipmentIsolationMethods(equipmentId: number): Promise<IsolationMethod[]> {
    const response = await fetch(`${API_BASE_URL}/sites/equipment/${equipmentId}/isolation-methods/`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.json();
  }

  /**
   * Convert isolation analysis to Access Permit isolation records format
   */
  convertAnalysisToIsolationRecords(analysis: IsolationAnalysisResult): Array<{
    id: string;
    sequence_number: number;
    equipment_code: string;
    isolation_point: string;
    isolation_method: string;
    lock_number: string;
    isolation_check_initial: string;
    issuer_initial: string;
    recipient_initial: string;
    amendment_issuer_initial: string;
    amendment_recipient_initial: string;
  }> {
    return analysis.isolation_analysis.map((isolation, index) => ({
      id: `auto-${isolation.isolation_point_id}`,
      sequence_number: index + 1,
      equipment_code: isolation.isolation_equipment_kks,
      isolation_point: `${isolation.energy_source_name} - ${isolation.isolation_equipment_name}`,
      isolation_method: isolation.isolation_method,
      lock_number: '', // To be filled by user
      isolation_check_initial: '',
      issuer_initial: '',
      recipient_initial: '',
      amendment_issuer_initial: '',
      amendment_recipient_initial: ''
    }));
  }

  /**
   * Convert analysis to Recipient Applied Safety Measures format
   */
  convertAnalysisToRecipientRecords(analysis: IsolationAnalysisResult): Array<{
    id: string;
    sequence_number: number;
    isolation_point: string;
    isolation_method: string;
    issuer_agreed_initial: string;
    lock_number: string;
    applied_initial: string;
    removed_initial: string;
  }> {
    return analysis.isolation_analysis.map((isolation, index) => ({
      id: `recipient-auto-${isolation.isolation_point_id}`,
      sequence_number: index + 1,
      isolation_point: `${isolation.energy_source_name} - ${isolation.isolation_equipment_name}`,
      isolation_method: isolation.isolation_method,
      issuer_agreed_initial: '',
      lock_number: '',
      applied_initial: '',
      removed_initial: ''
    }));
  }
}

// Export singleton instance
const isolationService = new IsolationService();
export default isolationService;